// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import type { AppliedFinding } from './core/apply';
import { findingsCsv, type CsvOptions, type FindingRow } from './core/csv';
import { maskPointerKeys } from './core/pointer';
import type { PseudonymEntry } from './core/protocol';
import { COUNTS, RULE_BY_ID, RULES } from './core/rules';
import type { RunState } from './core/types';

/** An object key that is itself an email or phone number leaks through the path column. */
function pointerSegmentIsSensitive(segment: string): boolean {
  return /@/.test(segment) || /^\+[1-9]\d{7,14}$/.test(segment);
}

export function toFindingRow(f: AppliedFinding): FindingRow {
  return {
    record: f.record,
    line: f.line,
    byteOffset: f.byteOffset,
    pointer: maskPointerKeys(f.pointer, pointerSegmentIsSensitive),
    inKey: f.inKey,
    rule: f.rule,
    severity: f.severity,
    tier: f.tier,
    matchLen: f.matchEnd - f.matchStart,
    validator: f.validator,
    action: f.suppressedBy !== '' ? 'suppressed' : f.action,
    // The replacement is a pseudonym or a marker — never a value.
    replacement: f.replacement,
    suppressedBy: f.suppressedBy,
    context: f.context,
  };
}

export function buildFindingsCsv(findings: readonly AppliedFinding[], opts: CsvOptions): string {
  let out = '';
  for (const chunk of findingsCsv(findings.map(toFindingRow), opts)) out += chunk;
  return out;
}

export interface MappingHeader {
  readonly source: string;
  readonly sourceBytes: number;
  readonly projectKey: string;
  readonly rules: Record<string, number>;
  readonly dismissedRules: string[];
  readonly normalisation: 'v1';
  readonly runState: RunState;
}

/**
 * The mapping payload: one NDJSON header line, then one line per distinct entity.
 *
 * This file is the only way to reverse a pseudonym, so it necessarily contains every raw value.
 * That is why it is sealed rather than merely written, and why its passphrase is generated at
 * nine words by default.
 */
export function buildMappingPayload(
  header: MappingHeader,
  entries: readonly PseudonymEntry[],
): Uint8Array {
  let text = JSON.stringify({ hdr: 1, ...header }) + '\n';
  for (const e of entries) {
    text +=
      JSON.stringify({
        t: e.type,
        p: e.pseudonym,
        v: e.normalised,
        n: e.count,
        first: { rec: e.firstRecord, path: e.firstPointer },
      }) + '\n';
  }
  return new TextEncoder().encode(text);
}

export interface ReportInput {
  readonly fileName: string;
  readonly fileBytes: number;
  readonly decompressedBytes: number;
  readonly records: number;
  readonly malformed: number;
  readonly counts: Map<string, number>;
  readonly distinct: Map<string, number>;
  readonly dismissed: ReadonlySet<string>;
  readonly enabledRules: ReadonlySet<string>;
  readonly runState: RunState;
  readonly keyFp: string;
  readonly entries: number;
  readonly startedAt: Date;
  readonly cleanWritten: number | null;
}

const HONEST_LIMITS = `WHAT THIS REPORT DOES NOT SAY
  A clean report means nothing matched. It does not mean the file is clean.

  Scrubjay has no language model, so it detected NO personal names and NO postal
  addresses. "Hi Alice, I've shipped it to 14 Bramham Road, Leeds" is unchanged.

  It scans string values, not JSON numeric literals, because there is no rewrite of a
  bare number that is both valid JSON and type-preserving.

  A checksum rules out a typo, not a coincidence: the US routing checksum accepts about
  one in ten random nine-digit strings, and US Social Security numbers have had no
  checksum at all since June 2011 — so an ordinary order number can match that rule.

  Pseudonymisation is not anonymisation. The output remains personal data: the same
  person has the same code everywhere, and the text around a code still describes them.

  Anyone holding the project key can match a candidate list against every pseudonym.`;

export function buildReport(r: ReportInput): string {
  const lines: string[] = [];
  const pad = (s: string, n: number): string => s.padEnd(n);

  lines.push('SCRUBJAY SCAN REPORT');
  lines.push('='.repeat(72));
  lines.push('');
  lines.push(`File            ${r.fileName}`);
  lines.push(`Size            ${r.fileBytes.toLocaleString('en-GB')} bytes on disk`);
  lines.push(`Read            ${r.decompressedBytes.toLocaleString('en-GB')} bytes decompressed`);
  lines.push(`Records         ${r.records.toLocaleString('en-GB')}`);
  if (r.malformed > 0) {
    lines.push(
      `Unparseable     ${r.malformed.toLocaleString('en-GB')} line(s) could not be parsed as JSON and were scanned as raw text`,
    );
  }
  lines.push(`Scanned         ${r.startedAt.toISOString()}`);
  lines.push(`Run state       ${r.runState}`);
  if (r.runState === 'INCOMPLETE') {
    lines.push('                *** The scan did not finish. The counts below cover only what was read. ***');
  }
  lines.push(`Project key fp  ${r.keyFp}`);
  lines.push(`Rules enabled   ${r.enabledRules.size} of ${COUNTS.total}`);
  lines.push('');

  const total = [...r.counts.values()].reduce((a, c) => a + c, 0);
  lines.push(`FINDINGS: ${total.toLocaleString('en-GB')}`);
  lines.push('-'.repeat(72));
  if (total === 0) {
    lines.push('  Nothing matched.');
  } else {
    lines.push(`  ${pad('RULE', 30)}${pad('SEVERITY', 10)}${pad('FINDINGS', 12)}${pad('DISTINCT', 10)}STATUS`);
    const sorted = [...r.counts.entries()].sort((a, b) => b[1] - a[1]);
    for (const [rule, n] of sorted) {
      const meta = RULE_BY_ID[rule];
      lines.push(
        `  ${pad(rule, 30)}${pad(meta?.severity ?? '?', 10)}${pad(n.toLocaleString('en-GB'), 12)}${pad(
          (r.distinct.get(rule) ?? 0).toLocaleString('en-GB'),
          10,
        )}${r.dismissed.has(rule) ? 'DISMISSED — not redacted' : 'redacted'}`,
      );
    }
  }
  lines.push('');
  lines.push(`Distinct entities in the mapping: ${r.entries.toLocaleString('en-GB')}`);
  if (r.cleanWritten !== null) {
    lines.push(`Cleaned file written: ${r.cleanWritten.toLocaleString('en-GB')} bytes`);
  } else {
    lines.push('Cleaned file: not produced on this run.');
  }
  lines.push('');

  const off = RULES.filter((x) => !r.enabledRules.has(x.id));
  if (off.length > 0) {
    lines.push('RULES THAT WERE NOT RUN');
    lines.push('-'.repeat(72));
    for (const x of off) lines.push(`  ${pad(x.id, 30)}${x.label}`);
    lines.push('');
  }

  lines.push(HONEST_LIMITS);
  lines.push('');
  lines.push('Produced by Scrubjay — https://scrubjay.benrichardson.dev');
  return lines.join('\n') + '\n';
}
